import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, BrainCircuit, Activity, Mic, Receipt, Package, Megaphone,
  Dumbbell, GraduationCap, ArrowRight, CheckCircle2,
  ShieldCheck, ChevronDown, Layers,
} from 'lucide-react';
import { BrandName } from '../components/BrandName';
import { BrandLogo } from '../components/BrandLogo';
import AppFooter from '../components/AppFooter';

/**
 * Iter 127 — Allied Healthcare Platform page. Deep dive on every module
 * shipped in Physiogenic today, reachable from the top-nav "About"
 * dropdown. Each section = one power feature with tangible outcomes,
 * not marketing filler.
 */
export default function AlliedHealthcarePlatform() {
  return (
    <div className="min-h-screen bg-[#FAF6EE] text-[#1A2332]">
      <TopBar/>

      {/* ─── Hero ────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-14 md:pt-24 pb-16 md:pb-24 px-6 md:px-10 bg-[#003D80] text-white">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #FFB000 0, transparent 40%), radial-gradient(circle at 80% 80%, #5DBB66 0, transparent 40%)' }}/>
        <div className="max-w-7xl mx-auto relative">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#FFB000] font-bold mb-4" data-testid="platform-eyebrow">
            The Physiogenic Platform
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white max-w-4xl" data-testid="platform-title">
            One <BrandName/> login for the whole <span className="text-[#FFB000]">allied&nbsp;healthcare</span> clinic.
          </h1>
          <p className="mt-6 text-lg text-white/80 max-w-2xl leading-relaxed" data-testid="platform-lede">
            An EHR that expects to be your ONLY software. Nine power modules — from AI Scribe
            to inventory to student supervision — all sitting on the same patient
            chart, same billing engine and same encryption boundary.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/start-clinic" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="platform-cta-start">
              Start free trial <ArrowRight className="w-4 h-4"/>
            </Link>
            <a href="#modules" className="text-sm px-5 py-2 rounded-lg border border-white/25 text-white hover:border-white transition inline-flex items-center gap-2" data-testid="platform-cta-jump">
              Jump to modules <ChevronDown className="w-4 h-4"/>
            </a>
          </div>

          {/* Quick module strip */}
          <div className="mt-14 grid grid-cols-3 md:grid-cols-9 gap-2" data-testid="platform-module-strip">
            {MODULES.map(m => (
              <a key={m.slug} href={`#${m.slug}`}
                 className="group border border-white/10 hover:border-[#FFB000]/60 hover:bg-white/5 transition rounded-lg p-3 text-center"
                 data-testid={`platform-chip-${m.slug}`}>
                <m.icon className="w-4 h-4 mx-auto mb-1.5 text-[#FFB000]"/>
                <div className="text-[10px] tracking-wide text-white/75 group-hover:text-white transition leading-tight">{m.short}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Modules — one deep-dive section per feature ─────── */}
      <div id="modules" className="max-w-7xl mx-auto">
        {MODULES.map((m, i) => (
          <ModuleSection key={m.slug} module={m} flip={i % 2 === 1}/>
        ))}
      </div>

      {/* ─── One chart, one engine ───────────────────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-24 bg-white/70 border-y border-[#E8DFC8]">
        <div className="max-w-5xl mx-auto text-center">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#003D80] font-bold mb-3 inline-flex items-center gap-2">
            <Layers className="w-3.5 h-3.5"/> The unifying idea
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl leading-tight mb-5">
            Nine modules. One patient. One bill. One privacy boundary.
          </h2>
          <p className="text-[#3A4A5E] leading-relaxed max-w-3xl mx-auto">
            Every feature above writes into the same patient chart under the same tenant
            isolation. When your reception books an appointment, the AI Scribe already
            knows the last visit's assessment; when your invoice fires, tax data
            populates automatically; when a student supervises, they inherit only
            the redacted read-only permission set. No copy-paste, no data leaks between
            modules, no integration surface to secure.
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-2 justify-center">
            {['GDPR', 'HIPAA', 'EHDS', 'MDR IIa', 'ISO 27001-aligned', '12 residency regions'].map(tag => (
              <span key={tag} className="text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border border-[#003D80]/25 text-[#003D80] bg-white/60">
                <ShieldCheck className="w-3 h-3 inline mr-1 -mt-0.5"/> {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-20 bg-[#003D80]">
        <div className="max-w-4xl mx-auto text-center">
          <BrandLogo size={64} src="/logo.jpg" className="mx-auto mb-4"/>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white leading-tight">
            See the whole platform in your own clinic in 60 seconds.
          </h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            No credit card. Bring your own patient list — full CSV import wizard included.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/start-clinic" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="platform-footer-start">
              Start your clinic free <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/about" className="text-sm px-5 py-2 rounded-lg border border-white/25 text-white hover:border-white transition inline-flex items-center gap-2" data-testid="platform-footer-about">
              Read about us <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </section>

      <AppFooter/>
    </div>
  );
}

/* ─── Module data ─────────────────────────────────────────────── */
const MODULES = [
  {
    slug: 'booking',
    short: 'Booking',
    icon: Calendar,
    accent: '#003D80',
    title: 'Online Booking & Smart Reminders',
    subtitle: 'Fill the calendar without the phone tag.',
    body: 'A public booking page per clinician (and per clinic) that respects your services, session lengths, buffers, blackout hours and holidays. SMS + email reminders fire 24 h and 1 h before the visit with a one-tap reschedule link. Waitlists auto-promote from cancellations.',
    bullets: [
      '15+ configurable session types with price × duration × buffer',
      'iCal + Google Calendar 2-way sync per clinician',
      'Configurable no-show fee via saved-card prepay',
      'Waitlist auto-promotion when a slot opens',
      'Multi-location booking — patient picks nearest clinic',
    ],
    image: 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Physiotherapist checking calendar on tablet',
  },
  {
    slug: 'ai',
    short: 'AI',
    icon: BrainCircuit,
    accent: '#7A1F3D',
    title: 'AI Scribe · AI Dx · Red-Flag Screening',
    subtitle: 'Three medical-grade AI assistants, one clinician.',
    body: 'Physiogenic ships three clinical AI features running on Claude Sonnet 4.6 + GPT-5.2 under our own privacy boundary — never on public LLM endpoints. AI Scribe converts a spoken assessment into a fully-formatted SOAP note. AI Dx suggests ICD-11 + ICF codes with confidence scores. Red-Flag Screening triages every new intake for serious pathology signals (cauda equina, cord compression, DVT, cancer red flags) before you even sit down with the patient.',
    bullets: [
      'AI Scribe — 2-minute audio → filed SOAP + billing codes',
      'AI Dx — ICD-11 + ICF suggestions with confidence + sources',
      'Red-Flag Screening on 100 % of new intakes',
      'Clinician always in the loop — every AI output editable + auditable',
      'Zero training on your data — contractual + technical guarantee',
    ],
    image: 'https://images.unsplash.com/photo-1655720845942-5ba26fadd90a?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'AI assistant interface for medical scribing',
  },
  {
    slug: 'posture',
    short: 'Posture AI',
    icon: Activity,
    accent: '#1B5E20',
    title: 'Posture Sensor Analysis',
    subtitle: 'Objective posture assessment in one photo.',
    body: 'Snap a front + side photo of the patient and Physiogenic overlays a 17-landmark skeletal model, computes shoulder-tilt, pelvic-tilt, forward-head, thoracic-kyphosis and Q-angle deviations in real degrees. Every result stores into the chart with a coloured overlay, so week-8 progress comparisons are automatic.',
    bullets: [
      '17-landmark real-time skeletal overlay',
      'Sagittal + frontal plane deviation in degrees',
      'Longitudinal progress overlay — week 1 vs week 8 side-by-side',
      'Patient-facing summary PDF with clinician commentary',
      'Runs on any modern phone camera — no proprietary hardware',
    ],
    image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Posture assessment with digital markup',
  },
  {
    slug: 'scribe',
    short: 'Voice → Text',
    icon: Mic,
    accent: '#003D80',
    title: 'Voice Recording → Filed Note',
    subtitle: 'Talk. We do the rest.',
    body: 'Physiogenic\'s ambient scribe records the whole consultation (with patient consent captured on-screen), transcribes it via medical-grade Whisper, and structures the output into a full SOAP note — subjective, objective, assessment, plan — inside the patient chart. Names, IDs, PHI-like tokens are automatically redacted before transcription leaves the encryption boundary.',
    bullets: [
      'On-device consent capture before recording starts',
      'Medical vocabulary Whisper model — 96 %+ accuracy',
      'Automatic PHI redaction pre-transcription',
      'Live transcript view during consult',
      'Files SOAP + billing codes + follow-up tasks in one tap',
    ],
    image: 'https://images.unsplash.com/photo-1590650153855-d9e808231d41?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Clinician using voice recording during consultation',
  },
  {
    slug: 'tax',
    short: 'Tax',
    icon: Receipt,
    accent: '#8A5A00',
    title: 'Financials & Tax Reports',
    subtitle: 'From SOAP note to tax return, one flow.',
    body: 'Every completed appointment triggers an invoice draft with your services, VAT rules (healthcare-exempt where applicable), and payer routing (patient · private insurer · state payer). Monthly close generates the P&L, income-tax pre-fill, and VAT-return export in your local format — Polish JPK_V7, German UStVA, Spanish Modelo 303 out of the box.',
    bullets: [
      'VAT-exempt handling for physiotherapy (PL/DE/ES defaults)',
      'JPK_V7, UStVA, Modelo 303 exports on one click',
      'Automatic outstanding-invoice ageing + polite chase emails',
      'Multi-currency for cross-border patients',
      'Stripe + Przelewy24 + bank-transfer reconciliation',
    ],
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Tax document workflow',
  },
  {
    slug: 'inventory',
    short: 'Inventory',
    icon: Package,
    accent: '#7A1F3D',
    title: 'Inventory & Product Sales',
    subtitle: 'Track tape, sell braces, never over-order.',
    body: 'Every consumable and retail item you keep in the clinic — kinesiology tape, resistance bands, orthopaedic pillows, supplement powders — lives in one inventory. Sales dock automatically to the patient\'s invoice, stock drops in real time, and re-order thresholds fire before you run out on a Friday.',
    bullets: [
      'Per-location stock tracking with transfers between sites',
      'Barcode + SKU + supplier catalog',
      'Auto re-order alerts by low-water threshold',
      'Product-margin dashboard — see what actually pays',
      'Sell products directly on patient invoices',
    ],
    image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Rehab equipment and inventory shelves',
  },
  {
    slug: 'marketing',
    short: 'Marketing',
    icon: Megaphone,
    accent: '#FFB000',
    title: 'Marketing Automation & Reviews',
    subtitle: 'Fill the funnel without hiring an agency.',
    body: 'Email + SMS drip campaigns keyed off patient events — post-visit satisfaction, missed-appointment re-engagement, birthday, six-week outcomes check-in. Reviews land automatically on your public profile, and referral tracking attributes new patients back to campaigns so you know what actually works.',
    bullets: [
      'Post-visit review requests → Google + on-site simultaneously',
      'Automated re-engagement for patients gone 12+ weeks',
      'Referral tracking with attribution + gift codes',
      'Public clinic profile SEO-optimised out of the box',
      'A/B campaign testing with cohort revenue analytics',
    ],
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Marketing dashboard with campaign metrics',
  },
  {
    slug: 'fitness',
    short: 'Fitness',
    icon: Dumbbell,
    accent: '#5DBB66',
    title: 'Fitness & Wellness Module',
    subtitle: 'Bridge from rehab to performance — under one roof.',
    body: 'When a patient graduates from injury rehab, they don\'t need to leave for a gym. The Fitness module gives you personal-training packages, class scheduling (yoga, pilates, mobility), membership billing (Class-Pass style), and an exercise library that expands with fitness-specific progressions — deadlift form checks, VO2max estimation, strength-benchmark tracking.',
    bullets: [
      '500+ exercise library with fitness + rehab progressions',
      'PT packages · block bookings · membership tiers',
      'Class calendar with capacity + waitlists',
      'Direct-to-consumer wellness upsell path from a rehab visit',
      'Cross-sell reporting — rehab-to-fitness conversion',
    ],
    image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Fitness training in a modern gym environment',
  },
  {
    slug: 'students',
    short: 'Students',
    icon: GraduationCap,
    accent: '#003D80',
    title: 'Students & Volunteers',
    subtitle: 'Grow the next generation of clinicians without the risk.',
    body: 'Physiotherapy schools and residency programs plug directly into your clinic. Students and volunteers get scoped read/write access to only the patients they\'re supervised on. Every note they file goes into a mandatory-review queue for the supervising clinician to sign off, with sign-off attached forever as an audit trail. Hours are tracked automatically for their coursework.',
    bullets: [
      'Scoped access — students see only their assigned patients',
      'Two-signature workflow: student → supervising clinician',
      'Automatic clinical-hours tracker per student for coursework',
      'Volunteer background-check + waiver storage',
      'Zero-cost seats — students don\'t consume a paid clinician seat',
    ],
    image: 'https://images.unsplash.com/photo-1584515933487-779824d29309?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Student clinician learning under supervision',
  },
];

/* ─── Presentational sub-components ────────────────────────────── */
function TopBar() {
  return (
    <div className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[#E8DFC8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="platform-brand-back">
          <BrandLogo size={48} src="/logo.jpg"/>
          <span className="font-heading font-bold tracking-tight text-lg"><BrandName/></span>
        </Link>
        <Link to="/" className="text-sm text-[#3A4A5E] hover:text-[#003D80] inline-flex items-center gap-1.5" data-testid="platform-home-link">
          <ArrowRight className="w-3.5 h-3.5 rotate-180"/> Back to homepage
        </Link>
      </div>
    </div>
  );
}

function ModuleSection({ module: m, flip }) {
  const Icon = m.icon;
  return (
    <section id={m.slug} className="px-6 md:px-10 py-16 md:py-24 scroll-mt-24" data-testid={`platform-mod-${m.slug}`}>
      <div className={`grid lg:grid-cols-12 gap-10 items-center ${flip ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <div className="lg:col-span-6">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-3 inline-flex items-center gap-2"
               style={{ color: m.accent }}>
            <Icon className="w-3.5 h-3.5"/> Module · {m.short}
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl leading-tight mb-3">{m.title}</h2>
          <p className="text-lg text-[#003D80] font-medium mb-5">{m.subtitle}</p>
          <p className="text-[#3A4A5E] leading-relaxed">{m.body}</p>
          <ul className="mt-6 space-y-2.5 text-sm">
            {m.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: m.accent }}/>
                <span className="text-[#1A2332]">{b}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6">
          {/* Iter 129 — replaced unbranded stock photos with an editorial visual
              tile per module. The tile mixes the module accent colour with a
              subtle grid + a large icon "hero", a small feature badge, and (if
              the module is one of the two "clinician-facing" ones) the branded
              PHYSIOGENIC therapist portrait as an anchor. Zero unbranded people
              on the page — every visual is either brand-driven typography or
              our own uniformed portrait. */}
          <ModuleVisual module={m} Icon={Icon}/>
        </div>
      </div>
    </section>
  );
}

/**
 * Editorial visual tile that replaces the old unbranded stock photos in
 * each module deep-dive. Design language:
 *   - Aspect 4:3 to match previous rhythm.
 *   - Module accent as the dominant colour on the tile.
 *   - Big translucent icon as the hero (feels like a large ClipboardCheck
 *     stamp behind the content).
 *   - A stack of 3 "sample UI chips" that hint at what the module actually
 *     shows in-app (booking slots, AI outputs, etc.).
 *   - Two modules — `ai` and `scribe` — additionally anchor the branded
 *     male portrait so the "person" side of the story stays present without
 *     any unbranded stock people.
 */
function ModuleVisual({ module: m, Icon }) {
  return (
    <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]"
         style={{ background: `linear-gradient(135deg, ${m.accent} 0%, ${m.accent}dd 45%, ${m.accent}88 100%)` }}>
      {/* Faint grid pattern */}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
           style={{
             backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
             backgroundSize: '32px 32px',
           }}/>
      {/* Big translucent hero icon */}
      <div className="absolute -bottom-8 -right-6 opacity-15">
        <Icon className="w-72 h-72 text-white" strokeWidth={1}/>
      </div>
      {/* Accent corner tag */}
      <div className="absolute top-4 left-4 z-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur border border-white/50">
          <Icon className="w-3.5 h-3.5" style={{ color: m.accent }}/>
          <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color: m.accent }}>{m.short}</span>
        </div>
      </div>
      {/* Sample UI chips — hint at real product surface */}
      <div className="absolute top-16 left-6 space-y-2 max-w-[70%] z-10">
        {(m.sampleChips || m.bullets.slice(0, 3)).map((chip, i) => (
          <div key={i}
               className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/95 backdrop-blur border border-white/50 text-[11px] font-semibold text-[#1A2332] mr-2"
               style={{ opacity: 1 - (i * 0.05) }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.accent }}/>
            {chip.length > 55 ? chip.slice(0, 55) + '…' : chip}
          </div>
        ))}
      </div>
      {/* Optional branded portrait anchor — removed per user request (iter 144):
          no people photos on the AI / voice-to-text module tiles. */}
      {/* Bottom badge — brand consistency call-out */}
      <div className="absolute bottom-4 left-6 z-10">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/25 backdrop-blur border border-white/15">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FFB000]"/>
          <span className="text-[10px] font-bold uppercase tracking-wider text-white">PHYSIOGENIC</span>
        </div>
      </div>
    </div>
  );
}

// End of file
